import { Currency } from '../../constants';
import { NEW_USER_FREESHIP_COUPON_CODE } from '../../constants/payment/coupon';
import { formatCurrency } from '../currency';
import type { Tier } from '../../constants/campaign/bmsm-types';
import { generateTierTitle } from '../../constants/campaign/bmsm';
import type { CartCouponResponse } from '../../model';

export interface MessagePart {
  text: string;
  bold: boolean;
}

/**
 * Parse message with bold markers
 *
 * Converts text with **bold markers** into an array of parts with bold flags.
 * Example: "Sign up to unlock **Free Shipping**!"
 * → [{ text: "Sign up to unlock ", bold: false }, { text: "Free Shipping", bold: true }, { text: "!", bold: false }]
 */
export function parseMessageWithBold(message: string): MessagePart[] {
  const parts: MessagePart[] = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(message)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: message.substring(lastIndex, match.index), bold: false });
    }
    parts.push({ text: match[1], bold: true });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < message.length) {
    parts.push({ text: message.substring(lastIndex), bold: false });
  }

  return parts;
}

/**
 * Check if user has new user free shipping coupon applied
 */
export function hasNewUserFreeShipCoupon(coupons: CartCouponResponse[]): boolean {
  return coupons.some((coupon) => coupon.code.toLowerCase() === NEW_USER_FREESHIP_COUPON_CODE);
}

/**
 * Check if this is a first order based on coupons
 */
export function inferIsFirstOrder(coupons: CartCouponResponse[]): boolean {
  return hasNewUserFreeShipCoupon(coupons);
}

export function buildMilestonesFromTiers(params: { tiers: Tier[]; cartTotalPrice: number; currency: Currency }): Array<{
  tier: Tier;
  title: string;
  achieved: boolean;
  statusText: string;
  anchorPercent: number;
}> {
  const { tiers, cartTotalPrice, currency } = params;
  const tierCount = tiers.length;

  return tiers.map((tier, index) => {
    const achieved = cartTotalPrice >= tier.threshold;
    const statusText = achieved ? 'Available' : formatCurrency(currency, tier.threshold);

    const anchorPercent = tierCount === 3 ? [0.2, 0.5, 0.8][index] : (index + 1) / (tierCount + 1);

    return {
      tier,
      title: generateTierTitle(tier),
      achieved,
      statusText,
      anchorPercent,
    };
  });
}

export function calculateProgress(params: { tiers: Tier[]; cartTotalPrice: number; isFreeShipping: boolean }): number {
  const { tiers, cartTotalPrice, isFreeShipping } = params;

  if (tiers.length === 0) return 0;

  const maxThreshold = Math.max(...tiers.map((t) => t.threshold));

  if (cartTotalPrice >= maxThreshold) {
    return 100;
  }

  const points: Array<{ threshold: number; percent: number }> = [{ threshold: 0, percent: 0 }];

  tiers.forEach((tier, index) => {
    const anchorPercent = tiers.length === 3 ? [20, 50, 80][index] : ((index + 1) / (tiers.length + 1)) * 100;

    points.push({
      threshold: tier.threshold,
      percent: anchorPercent,
    });
  });

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];

    if (cartTotalPrice < curr.threshold) {
      const range = curr.threshold - prev.threshold;
      if (range <= 0) return curr.percent;

      const ratio = (cartTotalPrice - prev.threshold) / range;
      const progress = prev.percent + ratio * (curr.percent - prev.percent);

      if (isFreeShipping && tiers[0]?.type === 'shipping') {
        return Math.max(progress, points[1]?.percent || 20);
      }

      return progress;
    }
  }

  return 100;
}

/**
 * Get bottom message for BMSM banner
 *
 * Returns a message string with **bold markers** for text that should be displayed in bold.
 * Use parseMessageWithBold() to convert the markers into formatted text parts.
 *
 * Bold markers: **text** will be rendered as bold text
 */
export function getBottomMessage(params: {
  isAuthenticated: boolean;
  isFirstOrder: boolean;
  tiers: Tier[];
  cartTotalPrice: number;
  shippingFee: number;
  cartTotalDiscount: number;
  currency: Currency;
}): string {
  const { isAuthenticated, isFirstOrder, tiers, cartTotalPrice, shippingFee, cartTotalDiscount, currency } = params;

  if (!isAuthenticated) {
    return 'Sign up to unlock **Free Shipping** and up to **15% OFF!**';
  }

  const firstTier = tiers[0];
  if (!firstTier) {
    return '';
  }

  const reachedFirstTier = cartTotalPrice >= firstTier.threshold;
  if (!reachedFirstTier) {
    if (isFirstOrder) {
      return 'First order? **Free Shipping** with no minimum spend!';
    }
    const amountNeeded = firstTier.threshold - cartTotalPrice;
    return `Spend ${formatCurrency(currency, amountNeeded)} more to get **Free Shipping!**`;
  }

  const nextTier = tiers.find((t) => cartTotalPrice < t.threshold);
  if (nextTier) {
    const amountNeeded = nextTier.threshold - cartTotalPrice;
    return `Spend ${formatCurrency(currency, amountNeeded)} more to get **${nextTier.discountPercent}% OFF!**`;
  }

  const savingAmount = cartTotalPrice > 0 ? shippingFee + cartTotalDiscount : shippingFee;
  return `**Free Shipping** & ${formatCurrency(currency, savingAmount)} saved so far! — let's see how far it can go!`;
}
